/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { MessageType } from 'CalltakingCoreApi';
import { LanguageChangeSource, LanguageOverride } from './language.model';

export interface TranslationLanguage {
    languageCode: string;
    name: string;
}

export interface TranslationParticipant {
    type: 'CALLER' | 'CALL_TAKER' | 'SYSTEM';
    callingPartyNumber?: string;
    userName?: string;
    userId?: string;
}

export interface TranslationMessage {
    messageId: string;
    text: string;
}

export interface TranslationResult {
    applied: boolean;
    translatedText: string | null;
    sourceLanguage: string | null;
    targetLanguage: string | null;
}

export interface TranslationCapabilityState {
    enabled: boolean;
    state: 'NOT_STARTED' | 'STARTED' | 'ENDED';
    baseLanguage: string | null;
    targetLanguage: string | null;
}

export interface LanguageChangeMessage {
    id: string;
    timestamp: Date;
    text: string;
}

export enum DisplayMessages {
    ALL = 'ALL',
    BASE_ONLY = 'BASE_ONLY',
    FOREIGN_ONLY = 'FOREIGN_ONLY'
}

export enum TranslationIndicator {
    SHOW_ALL = 'SHOW_ALL',
    HIDE = 'HIDE'
}

export enum TranslationIndicatorMode {
    ReceivedTranslated = 'RECEIVED_TRANSLATED',
    SentTranslated = 'SENT_TRANSLATED',
    SentSelfLanguageLogged = 'SENT_SELF_LANGUAGE_LOGGED'
}

export const DISPLAY_MESSAGES_OPTIONS: { value: DisplayMessages; label: string }[] = [
    { value: DisplayMessages.ALL, label: 'All messages' },
    { value: DisplayMessages.BASE_ONLY, label: 'English only' },
    { value: DisplayMessages.FOREIGN_ONLY, label: 'Foreign language only' }
];

export const TRANSLATION_INDICATOR_OPTIONS: { value: TranslationIndicator; label: string }[] = [
    { value: TranslationIndicator.SHOW_ALL, label: 'Show all' },
    { value: TranslationIndicator.HIDE, label: 'Hide' }
];

export interface TranslationSettings {
    displayMessages: DisplayMessages;
    translationIndicator: TranslationIndicator;
}

export interface MessageTranslationLabels {
    isTranslated: boolean;
    topCode: string | undefined;
    bottomCode: string | undefined;
    kind: TranslationIndicatorMode | undefined;
    foreignLanguageCode: string | undefined;
}

export interface MessageErrorParams {
    messageType: MessageType;
    message: string;
    callId: string;
    clusterName: string;
    predefined: boolean;
    language: string;
    userName?: string;
    sourceText?: string;
    sourceLanguage?: string;
    targetText?: string;
    targetLanguage?: string;
    messageLanguage?: string;
}

export interface MessageInputLanguageState {
    suggestedLanguages: TranslationLanguage[];
    restoredLanguage: string | null;
    restoredLanguageChangeSource: LanguageChangeSource | null;
    latestTimelineEntry: TranslationStateEntry | null;
    resolvedBannerLanguage: LanguageOverride | null;
}

export interface TranslationStateEntry {
    languageCode: string;
    changeSource: LanguageChangeSource;
    originMessageUuid: string;
    timestamp: number;
    isFirst: boolean;
}

export interface BannerLanguageInputs {
    manualLanguageOverride: ManualLanguageOverride | null;
    latestEntry: TranslationStateEntry | null;
    restoredLanguage: string | null;
    restoredLanguageChangeSource: LanguageChangeSource | null;
    fallbackLanguage: string | null;
    hasActiveTranslationError?: boolean;
    previousEntry?: TranslationStateEntry | null;
}

export interface ManualLanguageOverride {
    languageCode: string;
    timestamp: number;
}
